import { inject, Injectable } from '@angular/core';
import { SupabaseService } from '@app/shared/services';
import { TypeaheadItem } from '@app/shared/ui/typeahead/interfaces';
import { SupabaseQueryParams } from '@app/shared/utils/interfaces';
import { ID_VARIANT_SIZE } from '@app/shared/variants/constants';
import { Observable } from 'rxjs/internal/Observable';
import { from } from 'rxjs/internal/observable/from';
import { map } from 'rxjs/internal/operators/map';
import { switchMap } from 'rxjs/internal/operators/switchMap';

import { Product, Variant } from '../interfaces';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly supabaseService = inject(SupabaseService);

  loadProducts(queryParams: SupabaseQueryParams): Observable<{ products: Product[]; total: number }> {
    const { sortAscending, sortColumn, currentPage, itemsPerPage } = queryParams;
    const startIndex = currentPage * itemsPerPage - itemsPerPage;
    const endIndex = currentPage * itemsPerPage - 1;
    return this.supabaseService.supabase$.pipe(
      switchMap(supabase => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let request = supabase.from('products').select('*', { count: 'exact' }).gt('quantity', 0);
        switch (true) {
          case queryParams.sizes && queryParams.sizes.length > 0: {
            request = supabase.rpc(
              'find_products_with_char_and_qty',
              {
                _char_id: '3eea3737-0cf2-11ee-0a80-03670030dd78',
                _sizes: queryParams.sizes,
              },
              { count: 'exact' },
            );
            break;
          }
          case queryParams.sizes === null: {
            request = request.is('variants', null);
            break;
          }
        }
        if (queryParams.textSearch) request = request.ilike('fullName', `%${queryParams.textSearch}%`);
        return from(
          request
            .not('fullName', 'like', '%Утепленные%')
            .order('isOriginal', { ascending: false })
            .order(sortColumn, { ascending: sortAscending })
            .range(startIndex, endIndex),
        );
      }),
      map(({ data, count }) => ({
        products: (data ?? []) as Product[],
        total: count ?? 0,
      })),
    );
  }

  getProduct(id: string): Observable<Product> {
    return this.supabaseService.supabase$.pipe(
      switchMap(supabase => from(supabase.from('products').select().eq('id', id).maybeSingle())),
      map(({ data }) => data as Product),
    );
  }

  loadSizes(): Observable<TypeaheadItem<string>[]> {
    return this.supabaseService.supabase$.pipe(
      switchMap(supabase => from(supabase.from('products').select('variants', { count: 'exact' }).gt('quantity', 0))),
      map(({ data }) => data as { variants: Variant[] }[]),
      map(data => {
        const result: TypeaheadItem<string>[] = [];
        for (const item of data) {
          if (!Array.isArray(item.variants)) {
            // if (!result.some(({ value }) => value === null)) {
            //   result.push({
            //     key: 'O/S',
            //     value: null,
            //   });
            // }
            continue;
          }
          for (const variant of item.variants) {
            if (variant.quantity <= 0) continue;
            const characteristic = variant.characteristics.find(({ id }) => id === ID_VARIANT_SIZE);
            if (characteristic && !result.some(({ value }) => value === characteristic.value)) {
              result.push({
                key: characteristic.value,
                value: characteristic.value,
              });
            }
          }
        }
        return result;
      }),
      map(sizes => sizes.sort((a, b) => +(a.value?.split('-')[0] ?? 0) - +(b.value?.split('-')[0] ?? 0))),
    );
  }
}
